<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import ContactActivitiesAPI from 'dashboard/api/contactActivities';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ActivityFeedItem from './components/ActivityFeedItem.vue';
import TrackingEventDetailModal from './components/TrackingEventDetailModal.vue';

const props = defineProps({
  preview: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['viewAll']);

const { t } = useI18n();
const route = useRoute();

const activities = ref([]);
const totalCount = ref(0);
const page = ref(1);
const isLoading = ref(false);

const detailModalRef = ref(null);

const showEventDetail = event => {
  detailModalRef.value?.open(event);
};

const hasMore = computed(() => activities.value.length < totalCount.value);

const visibleActivities = computed(() =>
  props.preview ? activities.value.slice(0, 5) : activities.value
);

const fetchActivities = async (nextPage = 1, append = false) => {
  if (!route.params.contactId) return;
  isLoading.value = true;
  try {
    const { data } = await ContactActivitiesAPI.get(
      route.params.contactId,
      nextPage
    );
    totalCount.value = data.meta?.count || 0;
    activities.value = append
      ? [...activities.value, ...data.payload]
      : data.payload;
    page.value = nextPage;
  } catch (error) {
    if (!append) activities.value = [];
  } finally {
    isLoading.value = false;
  }
};

const loadMore = () => fetchActivities(page.value + 1, true);

onMounted(() => fetchActivities(1));
</script>

<template>
  <div class="flex flex-col">
    <div
      v-if="isLoading && !activities.length"
      class="flex justify-center py-6 text-n-slate-11"
    >
      <Spinner :size="20" />
    </div>
    <div v-else-if="activities.length" class="divide-y divide-n-weak">
      <ActivityFeedItem
        v-for="item in visibleActivities"
        :key="item.id"
        class="py-3"
        :item="item"
        @show-detail="showEventDetail"
      />
    </div>
    <p v-else class="py-6 text-sm text-center text-n-slate-11">
      {{ t('CONTACTS_LAYOUT.ACTIVITIES.EMPTY') }}
    </p>

    <Button
      v-if="preview && totalCount"
      variant="link"
      color="blue"
      size="sm"
      class="self-start mt-2 hover:no-underline"
      :label="t('CONTACTS_LAYOUT.OVERVIEW.VIEW_ALL')"
      @click="emit('viewAll')"
    />
    <Button
      v-else-if="!preview && hasMore"
      variant="outline"
      color="slate"
      size="sm"
      class="self-center mt-3"
      :is-loading="isLoading"
      :label="t('CONTACTS_LAYOUT.ACTIVITIES.LOAD_MORE')"
      @click="loadMore"
    />
  </div>

  <TrackingEventDetailModal ref="detailModalRef" />
</template>
